import type { ApolloError } from '@apollo/client';
import { parseNetworkErrorDetails } from '@shared/utils/parseNetworkErrorDetails';

export type CreateEventErrorType = 'clientBlock' | 'serverError' | 'rateLimit';

export const classifyCreateEventError = (error: ApolloError): CreateEventErrorType => {
  // Rate limit — check first, keep existing behavior.
  // `props` is optional: gateway-level errors (e.g. hive-router's pre-execution
  // validation errors) carry `extensions.code` with no `props`, and an unguarded
  // `.props.statusCode` throws on those. Matches checkGraphqlError.ts.
  if (error.graphQLErrors[0]?.extensions?.props?.statusCode === 429) {
    return 'rateLimit';
  }

  const { statusCode, isTimeout, isHtmlResponse } = parseNetworkErrorDetails(error);
  const message = error.message?.toLowerCase() ?? '';

  // Client-side block: fetch failures, network errors, offline
  const isFetchFailure = message.includes('failed to fetch') || message.includes('networkerror') || message.includes('network request failed') || message.includes('load failed');

  const isNetworkLevelError = error.networkError && !statusCode && !isTimeout && !isHtmlResponse;

  // WAF / CDN block: A 403 at the network level (not a GraphQL error) means the
  // request was rejected by CloudFront WAF before reaching our servers — typically
  // caused by VPN, adblocker, or flagged IP address.
  const isWafBlock = statusCode === 403;

  if (isFetchFailure || isNetworkLevelError || isWafBlock) {
    return 'clientBlock';
  }

  // Everything else: 5xx, timeout, HTML response, unknown → server error
  // Default to serverError to avoid blaming the user
  return 'serverError';
};
